import React from "react"
import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox"
import type {
  CheckboxRootChangeEventDetails,
  CheckboxRootState
} from "@base-ui/react/checkbox"
import { Icon, type IconName } from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { cva } from "class-variance-authority"
import { cn } from "../utils"

export type { CheckboxRootChangeEventDetails }

// =============================================================================
// Styles
// =============================================================================
// The glyph's 4px corner plus its 3px inset, so the ring stays concentric
// with the box. In rem so it scales with the box when the root font size does.
const FOCUS_INDICATOR_RADIUS = "0.4375rem"

const rootStyles = cva(
  [
    "relative inline-flex size-6 shrink-0 items-center justify-center transition-colors",
    "rounded-(--checkbox-focus-indicator-radius) outline-2 outline-transparent focus-visible:outline-gray-900",
    // A 40x40 tap target, without growing the 24px box the design draws
    "after:absolute after:-inset-2 after:content-['']"
  ],
  {
    variants: {
      tone: {
        empty: "cursor-pointer text-gray-600 hover:text-gray-900",
        filled: "cursor-pointer text-gray-900 hover:text-gray-800",
        disabled: "cursor-not-allowed text-gray-500"
      }
    },
    defaultVariants: { tone: "empty" }
  }
)

const toneFor = ({ checked, indeterminate, disabled }: CheckboxRootState) => {
  if (disabled) return "disabled" as const
  if (checked || indeterminate) return "filled" as const
  return "empty" as const
}

// =============================================================================
// Icons
// =============================================================================
const iconFor = ({ checked, indeterminate }: CheckboxRootState): IconName => {
  if (indeterminate) return "minus-square-filled"
  if (checked) return "check-square-filled"
  return "check-square-empty"
}

// =============================================================================
// Props
// =============================================================================
export type CheckboxProps = {
  /** Whether the checkbox is ticked. Pair with `onCheckedChange`. */
  checked?: boolean
  /** Whether the checkbox starts ticked, when left uncontrolled */
  defaultChecked?: boolean
  /** Called with the new checked state when the checkbox is ticked or unticked */
  onCheckedChange?: (
    checked: boolean,
    eventDetails: CheckboxRootChangeEventDetails
  ) => void
  /**
   * Neither ticked nor unticked, meaning "some but not all of the things below
   * are selected". Renders a minus and reports `aria-checked="mixed"`.
   */
  indeterminate?: boolean
  /** Whether the checkbox ignores user interaction */
  disabled?: boolean
  /** Id of the underlying input. A `Field` supplies one when this is omitted. */
  id?: string
  /** Identifies the checkbox when its form is submitted */
  name?: string
  /** The value submitted when the checkbox is ticked */
  value?: string
  /**
   * Names the checkbox for screen readers when it has no visible label, as in a
   * table's select column. Rendered as visually hidden text, so it goes through
   * translation like any other copy.
   */
  accessibleName?: string
  /** Id of the element that labels the checkbox, when one is already on screen */
  "aria-labelledby"?: string
  /** Id of the element that describes the checkbox */
  "aria-describedby"?: string
  /** Use `accessibleName` instead, so the name can be translated */
  "aria-label"?: never
  onClick?: React.MouseEventHandler<HTMLElement>
  onBlur?: React.FocusEventHandler<HTMLElement>
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * A checkbox.
 *
 * Renders the control on its own. Pair it with a `Field` to give it a label:
 *
 * @example
 * ```tsx
 * <Field orientation="horizontal">
 *   <Checkbox checked={value} onCheckedChange={setValue} />
 *   <Field.Label>Include a link back to my newsletter</Field.Label>
 * </Field>
 * ```
 */
export const Checkbox = React.forwardRef<HTMLElement, CheckboxProps>(
  function Checkbox({ accessibleName, className, ...props }, ref) {
    return (
      <CheckboxPrimitive.Root
        {...props}
        ref={ref}
        data-slot="checkbox"
        className={state => cn(rootStyles({ tone: toneFor(state) }), className)}
        style={
          {
            "--checkbox-focus-indicator-radius": FOCUS_INDICATOR_RADIUS
          } as React.CSSProperties
        }
      >
        {accessibleName && <VisuallyHidden>{accessibleName}</VisuallyHidden>}
        {/*
          Each state is a whole icon in the design, not one box with a tick
          laid over it, so the indicator stays mounted and the state picks a
          glyph rather than a tick appearing over a CSS-drawn box.
        */}
        <CheckboxPrimitive.Indicator
          keepMounted
          render={(_indicatorProps, state) => (
            <Icon name={iconFor(state)} data-slot="checkbox-indicator" />
          )}
        />
      </CheckboxPrimitive.Root>
    )
  }
)

Checkbox.displayName = "Checkbox"
